import React from 'react';

export interface BlockRendererProps {
  block: { settings: Record<string, any> };
  config: {
    anaRenk: string;
    aksanRenk: string;
    font: string;
    fontBoyutu: number;
    odemeKosullari?: string;
    gecerlilikNotu?: string;
    altBilgiNotu?: string;
  };
  offer?: any;
  offerType?: 'given' | 'received';
  companyInfo?: any;
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  try {
    if (dateString.includes('T')) {
      const [d] = dateString.split('T');
      const [y, m, day] = d.split('-');
      return `${day}.${m}.${y}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return new Intl.DateTimeFormat('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  } catch { return dateString; }
};

const mapStatus = (status: string | undefined): string => {
  if (!status) return '-';
  const map: Record<string, string> = { approved: 'Onaylandı', pending: 'Beklemede', rejected: 'Reddedildi', draft: 'Taslak' };
  return map[String(status).toLowerCase()] ?? String(status);
};

const CustomerInfoBlockRenderer: React.FC<BlockRendererProps> = ({ block, config, offer, offerType }) => {
  const s = block?.settings ?? {};
  const borderStyle = s.borderStyle === 'full' || s.borderStyle === 'none' || s.borderStyle === 'left-accent' ? s.borderStyle : 'left-accent';
  const showTaxInfo = s.showTaxInfo !== false;
  const showAddress = s.showAddress !== false;
  const showPhone = s.showPhone !== false;
  const showEmail = s.showEmail !== false;

  const isGiven = offerType === 'given';
  const account = isGiven ? offer?.account : offer?.supplier;
  const headerLabel = isGiven ? 'MÜŞTERİ BİLGİLERİ' : 'TEDARİKÇİ BİLGİLERİ';

  const companyName = account?.companyName ? String(account.companyName) : '';
  const taxOffice = account?.taxOffice ? String(account.taxOffice) : '';
  const taxNumber = account?.taxNumber ? String(account.taxNumber) : '';
  const address = account?.address ? String(account.address) : '';
  const phone = account?.phone ? String(account.phone) : '';
  const email = account?.email ? String(account.email) : '';

  const fs = config.fontBoyutu;
  const fontStack = `'${config.font}', Arial, sans-serif`;

  let border: React.CSSProperties = {};
  if (borderStyle === 'left-accent') {
    border = { borderLeft: `4px solid ${config.aksanRenk}`, borderTop: '1px solid #e8e8e8', borderRight: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8' };
  } else if (borderStyle === 'full') {
    border = { border: `1px solid ${config.aksanRenk}` };
  }

  const detailRow = (label: string, value: string) => (
    <tr key={label}>
      <td style={{ padding: '3px 8px', color: '#666', fontWeight: 600, fontSize: fs - 1, whiteSpace: 'nowrap' as const, borderBottom: '1px solid #eee' }}>{label}</td>
      <td style={{ padding: '3px 8px', fontSize: fs - 1, textAlign: 'right' as const, borderBottom: '1px solid #eee' }}>{value}</td>
    </tr>
  );

  const infoRow = (label: string, value: string): React.ReactNode => {
    if (!value) return null;
    return <div style={{ marginTop: 4, lineHeight: 1.4, fontSize: fs - 1 }}><span style={{ color: '#666', fontWeight: 600 }}>{label}: </span><span>{value}</span></div>;
  };

  return (
    <div style={{ display: 'flex', gap: 16, fontFamily: fontStack, fontSize: fs }}>
      {/* LEFT: Customer/Supplier info */}
      <div style={{ flex: 1, ...border, borderRadius: 4, padding: '12px 14px', backgroundColor: '#fafafa' }}>
        <div style={{ fontSize: fs - 2, fontWeight: 700, color: config.anaRenk, letterSpacing: 0.5, marginBottom: 6, textTransform: 'uppercase' as const }}>{headerLabel}</div>
        {companyName ? <div style={{ fontWeight: 700, fontSize: fs + 1, marginBottom: 2 }}>{companyName}</div> : null}
        {showTaxInfo && taxOffice ? infoRow('Vergi Dairesi', taxOffice) : null}
        {showTaxInfo && taxNumber ? infoRow('Vergi No', taxNumber) : null}
        {showAddress ? infoRow('Adres', address) : null}
        {showPhone ? infoRow('Telefon', phone) : null}
        {showEmail ? infoRow('E-posta', email) : null}
      </div>

      {/* RIGHT: Offer details (compact) */}
      <div style={{ width: 220, flexShrink: 0 }}>
        <div style={{ fontSize: fs - 2, fontWeight: 700, color: config.anaRenk, letterSpacing: 0.5, marginBottom: 6, textTransform: 'uppercase' as const }}>TEKLİF DETAYLARI</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, border: `1px solid ${config.aksanRenk}`, borderRadius: 4 }}>
          <tbody>
            {offer?.offerNumber ? detailRow('Teklif No', String(offer.offerNumber)) : null}
            {detailRow('Tarih', formatDate(offer?.date))}
            {detailRow('Geçerlilik', formatDate(offer?.validUntil))}
            {detailRow('Durum', mapStatus(offer?.status))}
            {detailRow('Para Birimi', offer?.currency ? String(offer.currency) : 'TRY')}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomerInfoBlockRenderer;
